/**
 * Digital Experience Monitoring — edge request volume per platform for one
 * subdomain (Owl `GetEdgeQueryDiagram`). Subdomain-scoped via the shared scope
 * bar; 7/14/30-day or custom range; Summary (one line) vs Divide (one line per
 * platform). A zone with no `ever_proxied` subdomain renders an empty state.
 */
import { useMemo, useState } from 'react';
import { Activity } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useWafEdgeQueryDiagram, useWafPlatforms } from '@/api/waf';
import { ChartCard, RefreshButton, Stack } from '../../_shared/AnalyticsKit';
import { formatCount } from '../../_shared/analytics-format';
import { WafScopeBar, type WafScope } from './WafScopeBar';
import { WafRangeControl, type ResolvedRange } from './WafRangeControl';
import { intervalForRange, edgeRows, type ChartMode } from './diagram-transform';
import { loadJSON, saveJSON } from './storage';
import styles from './WafAnalytics.module.css';

export function WafEdgeAnalytics({ serviceId }: { serviceId: string }) {
  const modeKey = `waf:${serviceId}:experience:mode`;
  const [scope, setScope] = useState<WafScope>({});
  const [range, setRange] = useState<ResolvedRange | null>(null);
  const [mode, setMode] = useState<ChartMode>(() => loadJSON<ChartMode>(modeKey, 'summary'));

  const domain = scope.subdomain;
  // A zone is selected but has no proxied subdomain → nothing to chart.
  const noSubdomain = !!scope.zone && !domain;
  const interval = range ? intervalForRange(range.start_time, range.end_time) : 86400;

  const platformsQ = useWafPlatforms(serviceId);
  const platformLabel = useMemo(() => {
    const map = new Map((platformsQ.data ?? []).map((p) => [p.id, p.display_name || p.name]));
    // Platform id 0 = served from origin (no CDN platform).
    return (id: string) => (id === '0' ? 'Origin' : map.get(id) ?? id);
  }, [platformsQ.data]);

  const q = useWafEdgeQueryDiagram(
    serviceId,
    {
      domain: domain ?? '',
      start_time: range?.start_time ?? 0,
      end_time: range?.end_time ?? 0,
      interval,
    },
    !!domain && !!range,
  );

  const { rows, series } = useMemo(
    () => edgeRows(q.data, interval, platformLabel),
    [q.data, interval, platformLabel],
  );

  const changeMode = (m: ChartMode) => {
    setMode(m);
    saveJSON(modeKey, m);
  };

  return (
    <Container size="xl">
      <PageHeader
        icon={<Activity size={22} />}
        title="Digital Experience Monitoring"
        description="Edge request volume for the selected subdomain."
      />

      <div className={styles.toolbar}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
        <div className={styles.controls}>
          {/* TODO: add a Chart filter here (select which metric/platform series the
              chart renders) — Digital Experience Monitoring only. */}
          <WafRangeControl serviceId={serviceId} pageKey="experience" onChange={setRange} />
          <div className={styles.controlsRight}>
            <Select value={mode} onValueChange={(v) => changeMode(v as ChartMode)}>
              <SelectTrigger aria-label="Chart mode" className={styles.modeSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="summary">Summary</SelectItem>
                <SelectItem value="divide">Divide</SelectItem>
              </SelectContent>
            </Select>
            <RefreshButton onRefresh={() => q.refetch()} />
          </div>
        </div>
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<Activity size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain to monitor. Proxy a record to see edge analytics."
        />
      ) : (
        <Stack>
          <div className={mode === 'divide' ? styles.denseFill : undefined}>
            <ChartCard
              title="Edge requests"
              description="Edge request volume over the selected range."
              data={rows}
              xKey="t"
              series={series}
              type={mode === 'divide' ? 'area' : 'line'}
              height={280}
              stacked={mode === 'divide'}
              showLegend={false}
              // `key` is set for the tooltip only → unit there, clean Y-axis ticks.
              valueFormatter={(v, key) =>
                key ? `${formatCount(Number(v))} optimizations` : formatCount(Number(v))
              }
              loading={q.isLoading}
            />
          </div>
        </Stack>
      )}
    </Container>
  );
}

export default WafEdgeAnalytics;
